import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { cn } from "@/lib/utils";
import IncomeTaxChart from '../../charts/IncomeTaxChart';
import { IncomeTaxCalculatorAPIResponse } from '../../Types/Autogenerated/IncomeTaxCalculatorApiResponse';
import { IncomeTaxCalculatorRequestPayload } from '../../Types/Autogenerated/IncomeTaxCalculatorRequestPayload';
import { IncomesAdjustmentsAndDeductions } from '../../Types/Autogenerated/IncomesAdjustmentsAndDeductions';
import { Convert as FilingStatusConvert } from '../../Types/Autogenerated/FilingStatus';
import { Convert as StateConvert } from '../../Types/Autogenerated/State';
import { FormData } from '../../App';
import { CurrencyInputOnChangeValues } from 'react-currency-input-field';
import { taxYearItems, stateItems, filingStatuses } from '../../Utils/Constants';
import TermsDescriptions from '../../components/TermsDescriptions';
import { incomeTaxTerms } from '../../Utils/TermDefinitions';
import FloatingChartButton from '../../components/FloatingChartButton';
import ChartSummary from '../../components/ChartSummary';
import { generateIncomeTaxSummary } from '../../Utils/ChartSummaryGenerators';
import { formatCurrency } from '../../Utils/chartUtils';

interface IncomeTaxCalculatorPageProps {
  formData: FormData;
  selectedPage: string;
  personsToggle: boolean;
  showSnackbar: boolean;
  snackbarMessage: string;
  chartData: number[][] | null;
  handleClose: (event: React.SyntheticEvent | Event, reason?: string) => void;
  setChartData: (value: React.SetStateAction<number[][] | null>) => void;
  setSnackbarMessage: (value: React.SetStateAction<string>) => void;
  setShowSnackbar: (value: React.SetStateAction<boolean>) => void;
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onValueChange: (value: string | undefined, name?: string, values?: CurrencyInputOnChangeValues) => void;
  handleToggleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}

const IncomeTaxCalculatorPage: React.FC<IncomeTaxCalculatorPageProps> = ({
  formData,
  selectedPage,
  personsToggle,
  showSnackbar,
  snackbarMessage,
  chartData,
  handleClose,
  setChartData,
  setSnackbarMessage,
  setShowSnackbar,
  handleInputChange,
  onValueChange,
  handleToggleChange,
  handleSelectChange,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [grossIncome, setGrossIncome] = useState<number>(0);

  const formatNumber = (num: number | string) => {
    if (typeof num === 'string') {
      num = parseFloat(num.replace(/,/g, ''));
    }
    return isNaN(num) ? '' : num.toLocaleString('en-US');
  };

  const handleIncomeChange = (value: string, name: string) => {
    const numericValue = value ? parseFloat(value.replace(/,/g, '')) : 0;
    onValueChange(numericValue.toString(), name);
  };

  const processChartData = useCallback((data: IncomeTaxCalculatorAPIResponse): number[][] => {
    return [[
      data.medicare.income_tax_owed.reduce((a, b) => a + b, 0),
      data.social_security.income_tax_owed.reduce((a, b) => a + b, 0),
      data.state.income_tax_owed.reduce((a, b) => a + b, 0),
      data.federal.income_tax_owed.reduce((a, b) => a + b, 0),
      data.total_tax_owed,
    ]];
  }, []);

  const makeIncomeTaxCalculatorUrl = () => {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://127.0.0.1:5000/TakehomePayCalculator';
    }
    
    // Use predictable CloudFront URL
    const domain = window.location.hostname === 'moneymath.fyi' 
      ? 'moneymath.fyi' 
      : window.location.hostname;
    
    return `https://${domain}/api/takehome-pay`;
  };

  // Memoize input data to prevent unnecessary re-renders
  const inputData = useMemo(() => ({
    income: personsToggle ? [formData.incomePerson1, formData.incomePerson2] : [formData.income],
    taxYear: formData.taxYear,
    state: formData.state,
    filingStatus: formData.filingStatus,
    personsToggle
  }), [
    formData.income,
    formData.incomePerson1,
    formData.incomePerson2,
    formData.taxYear,
    formData.state,
    formData.filingStatus,
    personsToggle
  ]);

  const makeAPIRequest = useCallback(async () => {
    if (isLoading) return; // Prevent multiple simultaneous requests
    
    setIsLoading(true);
    try {
      const url = makeIncomeTaxCalculatorUrl();

      const incomes_adjustments_and_deductions: IncomesAdjustmentsAndDeductions[] = !inputData.personsToggle
        ? [{
            salaries_and_wages: Number(inputData.income[0]),
          }]
        : [{
            salaries_and_wages: Number(inputData.income[0]),
          }, {
            salaries_and_wages: Number(inputData.income[1]),
          }];

      const payload: IncomeTaxCalculatorRequestPayload = {
        tax_year: parseInt(inputData.taxYear, 10),
        state: StateConvert.toState(JSON.stringify(inputData.state)),
        filing_status: FilingStatusConvert.toFilingStatus(JSON.stringify(inputData.filingStatus)),
        incomes_adjustments_and_deductions: incomes_adjustments_and_deductions,
        state_data: { exemptions: 0 }
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Unknown error occurred');
      }

      const data: IncomeTaxCalculatorAPIResponse = await response.json();
      
      // Calculate and store gross income for summary
      const totalGrossIncome = personsToggle 
        ? Number(inputData.income[0]) + Number(inputData.income[1])
        : Number(inputData.income[0]);
      setGrossIncome(totalGrossIncome);
      
      setChartData(processChartData(data));
    } catch (error) {
      console.error('Error:', error);
      setSnackbarMessage(error instanceof Error ? error.message : 'An unknown error occurred');
      setShowSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  }, [inputData, processChartData, setChartData, setSnackbarMessage, setShowSnackbar, isLoading, personsToggle]);

  // Track previous input data for change detection
  const previousInputDataRef = useRef<string>('');

  // Auto-calculate with debouncing for API calls - only when inputs actually change
  useEffect(() => {
    const currentInputDataString = JSON.stringify(inputData);
    
    // Only make API request if input data has actually changed
    if (currentInputDataString !== previousInputDataRef.current) {
      const timeoutId = setTimeout(() => {
        makeAPIRequest();
        previousInputDataRef.current = currentInputDataString;
      }, 500); // 500ms debounce for quick API calls

      return () => clearTimeout(timeoutId);
    }
  }, [inputData, makeAPIRequest]);

  const labels: string[] = [
    "Medicare",
    "Social Security",
    "State",
    "Federal",
    "Total Tax",
  ];

  const sortedTaxYears = Object.entries(taxYearItems)
    .sort((a, b) => parseInt(b[1]) - parseInt(a[1]));

  const chartRef = useRef<HTMLDivElement>(null);

  return (
    <div className="income-tax-calculator w-full px-2 sm:px-4 lg:px-6 financial-app">
      {/* Professional Header */}
      <div className="hero-section mb-6 -mx-2 sm:-mx-4 lg:-mx-6 px-2 sm:px-4 lg:px-6">
        <div className="professional-layout">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-2 tracking-tight">
            {selectedPage}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Calculate your detailed federal, state, and payroll tax breakdown for accurate planning
          </p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 mb-6">
        {/* Form Section */}
        <div className="lg:w-2/5 min-w-0">
          <Card className="w-full shadow-lg hover:shadow-xl transition-all duration-300">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="w-2 h-8 bg-gradient-to-b from-primary to-accent rounded-full"></div>
                <h2 className={cn("text-xl font-semibold text-card-foreground")}>Tax Calculator</h2>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Enter your income and tax details for comprehensive tax analysis
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Income Configuration */}
              <div className="p-4 rounded-lg bg-muted/30 border border-border">
                <div className={cn("flex items-center justify-between")}>
                  <div>
                    <Label htmlFor="personsToggle" className="text-sm font-medium text-foreground">
                      Multiple Income Sources
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Calculate taxes for dual income households
                    </p>
                  </div>
                  <Switch
                    id="personsToggle"
                    checked={personsToggle}
                    onCheckedChange={(checked) => handleToggleChange({
                      target: { name: 'personsToggle', checked, id: 'persons', type: 'checkbox' }
                    } as React.ChangeEvent<HTMLInputElement>)}
                  />
                </div>
              </div>
              
              {/* Income Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-semibold text-foreground">Income Details</h3>
                  <div className="h-px bg-border flex-1"></div>
                </div>
                
                <div className={cn("grid gap-6", personsToggle ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1")}>
                  <div className="space-y-2">
                    <Label htmlFor={personsToggle ? "incomePerson1" : "income"} className="text-sm font-medium text-foreground flex items-center gap-2">
                      {personsToggle ? "Primary Annual Income" : "Annual Gross Income"}
                      <span className="text-xs text-muted-foreground">(before taxes)</span>
                    </Label>
                    <div className={cn("relative group")}>
                      <span className={cn("absolute inset-y-0 left-0 flex items-center pl-3 text-muted-foreground text-sm group-focus-within:text-primary transition-colors")}>$</span>
                      <Input
                        id={personsToggle ? "incomePerson1" : "income"}
                        name={personsToggle ? "incomePerson1" : "income"}
                        type="text"
                        placeholder="75,000"
                        value={formatNumber(personsToggle ? formData.incomePerson1 : formData.income)}
                        onChange={(e) => handleIncomeChange(e.target.value, personsToggle ? 'incomePerson1' : 'income')}
                        className={cn("pl-8 text-lg font-medium")}
                      />
                    </div>
                  </div>

                  {personsToggle && (
                    <div className={cn("space-y-2 border-l-2 border-border pl-4")}>
                      <Label htmlFor="incomePerson2" className="text-sm font-medium text-foreground flex items-center gap-2">
                        Secondary Annual Income
                        <span className="text-xs text-muted-foreground">(before taxes)</span>
                      </Label>
                      <div className={cn("relative group")}>
                        <span className={cn("absolute inset-y-0 left-0 flex items-center pl-3 text-muted-foreground text-sm group-focus-within:text-primary transition-colors")}>$</span>
                        <Input
                          id="incomePerson2"
                          name="incomePerson2"
                          type="text"
                          placeholder="50,000"
                          value={formatNumber(formData.incomePerson2)}
                          onChange={(e) => handleIncomeChange(e.target.value, 'incomePerson2')}
                          className={cn("pl-8 text-lg font-medium")}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
                
              {/* Tax Configuration Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-semibold text-foreground">Tax Configuration</h3>
                  <div className="h-px bg-border flex-1"></div>
                </div>
                
                <div className={cn("grid grid-cols-1 sm:grid-cols-2 gap-4")}>
                  <div className="space-y-2">
                    <Label htmlFor="taxYear" className="text-sm font-medium text-foreground">Tax Year</Label>
                    <Select 
                      value={formData.taxYear} 
                      onValueChange={(value) => handleSelectChange({ target: { name: 'taxYear', value } } as React.ChangeEvent<HTMLSelectElement>)}
                    >
                      <SelectTrigger id="taxYear" className="h-11">
                        <SelectValue placeholder="Select year" />
                      </SelectTrigger>
                      <SelectContent>
                        {sortedTaxYears.map(([key, value]) => (
                          <SelectItem key={value} value={value}>{key}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="state" className="text-sm font-medium text-foreground">State of Residence</Label>
                    <Select 
                      value={formData.state} 
                      onValueChange={(value) => handleSelectChange({ target: { name: 'state', value } } as React.ChangeEvent<HTMLSelectElement>)}
                    >
                      <SelectTrigger id="state" className="h-11">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(stateItems).map(([key, value]) => (
                          <SelectItem key={value} value={value}>{key}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="filingStatus" className="text-sm font-medium text-foreground">Filing Status</Label>
                  <Select 
                    value={formData.filingStatus} 
                    onValueChange={(value) => handleSelectChange({ target: { name: 'filingStatus', value } } as React.ChangeEvent<HTMLSelectElement>)}
                  >
                    <SelectTrigger id="filingStatus" className="h-11">
                      <SelectValue placeholder="Select filing status" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(filingStatuses).map(([key, value]) => (
                        <SelectItem key={key} value={key}>{value}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Enhanced Chart Section */}
        <div className="lg:w-3/5 min-w-0" ref={chartRef}>
          <Card className="w-full h-full shadow-lg hover:shadow-xl transition-all duration-300">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-8 bg-gradient-to-b from-accent to-primary rounded-full"></div>
                  <div>
                    <h2 className="text-xl font-semibold text-card-foreground">Tax Breakdown</h2>
                    <p className="text-sm text-muted-foreground">
                      Detailed breakdown of your federal, state, and payroll taxes
                    </p>
                  </div>
                </div>
                {chartData && !isLoading && (
                  <div className="text-right">
                    <div className="text-sm text-muted-foreground">Total Tax</div>
                    <div className="text-2xl font-bold text-primary">
                      {formatCurrency(chartData[0]?.[4] || 0)}
                    </div>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="h-full">
              {chartData && !isLoading ? (
                <div className="h-full flex flex-col">
                  <div className="flex-grow min-h-0">
                    <IncomeTaxChart labels={labels} chartData={chartData} />
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center min-h-[400px]">
                  <div className="flex flex-col items-center gap-4">
                    {isLoading && (
                      <div className="relative">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary/20 border-t-primary"></div>
                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primary/10 to-accent/10"></div>
                      </div>
                    )}
                    <div className="text-center">
                      <div className="text-lg font-medium text-foreground mb-1">
                        {isLoading ? 'Calculating your taxes...' : 'Ready to calculate'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {isLoading ? 'Processing tax calculations' : 'Enter your income and tax details to see breakdown'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Enhanced Chart Summary Section */}
      {chartData && !isLoading && (
        <div className="mt-6">
          <Card className="shadow-lg">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-2 h-8 bg-gradient-to-b from-success to-primary rounded-full"></div>
                <div>
                  <h2 className="text-xl font-semibold text-card-foreground">Tax Analysis</h2>
                  <p className="text-sm text-muted-foreground">
                    Detailed breakdown of your complete tax obligation
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ChartSummary 
                summaryItems={generateIncomeTaxSummary(chartData, grossIncome)}
                title=""
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Professional Terms Section */}
      <div className="mt-6">
        <Card className="shadow-lg">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-2 h-8 bg-gradient-to-b from-muted-foreground to-secondary rounded-full"></div>
              <div>
                <h2 className="text-xl font-semibold text-card-foreground">Understanding Tax Calculations</h2>
                <p className="text-sm text-muted-foreground">
                  Learn about the different components of your tax obligation
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <TermsDescriptions 
              terms={incomeTaxTerms} 
              title=""
            />
          </CardContent>
        </Card>
      </div>
      
      {/* Enhanced Error Display */}
      {showSnackbar && (
        <div className="fixed bottom-4 left-4 right-4 z-50 max-w-md mx-auto">
          <div className="bg-destructive text-destructive-foreground p-4 rounded-lg shadow-lg border border-destructive/20 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-destructive-foreground rounded-full"></div>
              <div className="font-medium">Calculation Error</div>
            </div>
            <div className="mt-1 text-sm opacity-90">
              {snackbarMessage}
            </div>
          </div>
        </div>
      )}
      
      <FloatingChartButton
        chartRef={chartRef}
        chartData={chartData}
      />
    </div>
  );
};

export default IncomeTaxCalculatorPage;
